import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, Sparkles, ArrowRight } from "lucide-react";

export default function Approach() {
  return (
    <section id="approach" className="py-20 md:py-32" data-testid="approach-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">
            Our Approach
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] font-extrabold leading-[1.04] text-slate-900 mb-6">
            The Delta <br />
            <span className="font-editorial italic font-medium text-slate-700">Field Force</span> Model.
          </h2>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">
            Young pharmacy graduates from Tier 2–3 cities, comprehensively trained, locally rooted —
            delivering enterprise-grade quality at optimized economics. A new generation of
            field force, engineered for the markets traditional reps overlook.
          </p>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="p-5 border border-slate-200 bg-white">
              <GraduationCap className="h-6 w-6 text-indigo-500 mb-3" strokeWidth={1.5} />
              <div className="font-display font-bold text-slate-900">Tier 2–3 Talent</div>
              <div className="text-sm text-slate-600 mt-1">Pharmacy graduates with local fluency.</div>
            </div>
            <div className="p-5 border border-slate-200 bg-white">
              <Sparkles className="h-6 w-6 text-teal-600 mb-3" strokeWidth={1.5} />
              <div className="font-display font-bold text-slate-900">Performance Economics</div>
              <div className="text-sm text-slate-600 mt-1">Revenue-share aligned to growth.</div>
            </div>
          </div>

          <Link
            to="/approach"
            data-testid="approach-learn-more"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 border-b border-slate-900 pb-1 hover:text-teal-700 hover:border-teal-700 transition-colors"
          >
            Explore our full approach <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Lifecycle Curve */}
        <div className="lg:col-span-7">
          <LifecycleCurve />
        </div>
      </div>
    </section>
  );
}

function LifecycleCurve() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7 }}
      className="relative bg-[#0B0F19] text-white p-8 md:p-10 overflow-hidden"
      data-testid="lifecycle-curve"
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-300 mb-2">
        Brand Lifecycle
      </div>
      <div className="flex items-baseline justify-between gap-4 mb-8">
        <div className="font-display text-2xl md:text-3xl font-bold">Medstry vs Traditional</div>
        <div className="hidden md:flex items-center gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-4 bg-teal-400 inline-block" /> Medstry
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-4 bg-slate-500 inline-block" /> Traditional
          </span>
        </div>
      </div>

      <svg viewBox="0 0 600 280" className="w-full h-auto" aria-label="Brand lifecycle curve">
        {/* Grid */}
        {[0, 1, 2, 3, 4].map((i) => (
          <line key={i} x1="0" x2="600" y1={50 + i * 50} y2={50 + i * 50} stroke="#1f2937" strokeWidth="1" />
        ))}
        {/* Traditional decline */}
        <path
          d="M 20 200 C 100 110, 200 80, 280 90 C 360 100, 440 160, 580 240"
          stroke="#64748b"
          strokeWidth="2.5"
          fill="none"
          strokeDasharray="6 6"
        />
        {/* Medstry curve */}
        <motion.path
          d="M 20 200 C 100 110, 200 80, 280 90 C 360 100, 460 70, 580 40"
          stroke="#0D9488"
          strokeWidth="3.5"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: "easeInOut" }}
        />
        {/* End dots */}
        <circle cx="580" cy="40" r="6" fill="#0D9488" />
        <circle cx="580" cy="240" r="5" fill="#64748b" />

        {/* Labels */}
        <text x="20" y="40" fill="#94a3b8" fontSize="11" fontWeight="600">REVENUE</text>
        <text x="20" y="265" fill="#94a3b8" fontSize="11" fontWeight="600">LAUNCH → MATURE → REVITALIZED</text>

        <text x="455" y="32" fill="#5eead4" fontSize="11" fontWeight="700">+ Untapped Potential</text>
        <text x="455" y="240" fill="#94a3b8" fontSize="11" fontWeight="600">— Decline</text>
      </svg>

      <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-2xl font-display font-extrabold tnum">1–2</div>
          <div className="text-xs text-slate-400">Years to breakeven</div>
        </div>
        <div>
          <div className="text-2xl font-display font-extrabold tnum">40–50%</div>
          <div className="text-xs text-slate-400">OPEX reduction</div>
        </div>
        <div>
          <div className="text-2xl font-display font-extrabold tnum">300+</div>
          <div className="text-xs text-slate-400">Market HQs</div>
        </div>
      </div>
    </motion.div>
  );
}
